import { useEffect, useMemo, useState } from 'react'

import {
  Field,
  FieldError,
  Input,
  Label,
  Modal,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  Text
} from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { ProductPicker } from '../../../components/advertiserBrand/ProductPicker'
import {
  ProductSelection,
  toProductInput
} from '../../../components/advertiserBrand/ProductPicker/types'
import styles from '../../../components/advertiserBrand/styles.module.css'
import {
  useAdvertiserBrandMutations,
  useAdvertiserBrandOptions
} from '../../../hooks/useAdvertiserBrands'

export type NewBrandModalState =
  | { mode: 'create' }
  | { mode: 'edit'; brand: AdvertiserBrand }
  | null

type NewBrandModalProps = {
  state: NewBrandModalState
  onClose: () => void
  /** A seleção da listagem é indexada por nome: o antigo deixa de existir. */
  onRenamed?: (previousName: string) => void
}

/**
 * Criar marca é nome + os produtos que a compõem: a API não persiste marca sem
 * produto. Renomear mexe só no nome.
 */
export const NewBrandModal: React.FC<NewBrandModalProps> = ({
  state,
  onClose,
  onRenamed
}) => {
  const { t } = useAPPTranslation()
  const { createBrand, renameBrand } = useAdvertiserBrandMutations()
  // a lista inteira, não a página corrente: nome repetido em qualquer página conta
  const { brands } = useAdvertiserBrandOptions()

  const isOpen = state != null
  const isEdit = state?.mode === 'edit'
  const currentName = isEdit ? state.brand.name : ''

  const [name, setName] = useState(currentName)
  const [selected, setSelected] = useState<ProductSelection>({})
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    setName(currentName)
    setSelected({})
    setSubmitted(false)
  }, [currentName, state])

  const nameError = useMemo<string | null>(() => {
    if (!name.trim())
      return String(t('rm:advertiserBrands.form.name.error.required'))

    const isDuplicated = brands.some(
      existing =>
        existing.name.trim().toUpperCase() === name.trim().toUpperCase() &&
        existing.name !== currentName
    )

    return isDuplicated
      ? String(t('rm:advertiserBrands.form.name.error.duplicated'))
      : null
  }, [brands, currentName, name, t])

  const selectedRows = Object.values(selected)
  const isPending = createBrand.isPending || renameBrand.isPending

  const handleSubmit = async () => {
    setSubmitted(true)

    if (nameError || (!isEdit && !selectedRows.length)) return

    if (isEdit) {
      const previousName = state.brand.name
      const nextName = name.trim()

      await renameBrand.mutateAsync({
        brand: previousName,
        new_brand: nextName
      })

      if (nextName !== previousName) onRenamed?.(previousName)
    } else {
      await createBrand.mutateAsync({
        brand: name.trim(),
        products: selectedRows.map(row => toProductInput(row, 'catalog'))
      })
    }

    onClose()
  }

  const submitKey = isEdit
    ? 'rm:advertiserBrands.form.submit.edit'
    : 'rm:advertiserBrands.form.submit.create'
  const submitLabel = t(submitKey, { count: selectedRows.length })

  const nameField = (
    <div className={styles.brandNameField}>
      <Field className={styles.brandField} error={submitted && !!nameError}>
        <Label>{t('rm:advertiserBrands.form.name.label')}</Label>
        <Input
          value={name}
          onChange={setName}
          placeholder={t('rm:advertiserBrands.form.name.placeholder')}
        />
        {submitted && !!nameError && <FieldError>{nameError}</FieldError>}
      </Field>
    </div>
  )

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size={isEdit ? 'small' : 'large'}
      className={isEdit ? styles.brandModal : styles.pickerModal}
    >
      <ModalHeader>
        <div className={styles.modalHeading}>
          <ModalHeading>
            {t(
              isEdit
                ? 'rm:advertiserBrands.form.title.edit'
                : 'rm:advertiserBrands.form.title.create'
            )}
          </ModalHeading>
          {!isEdit && (
            <Text variant="body" className={styles.description}>
              {t('rm:advertiserBrands.form.subtitle.create')}
            </Text>
          )}
        </div>
        <ModalDismiss />
      </ModalHeader>

      <ModalContent className={isEdit ? undefined : styles.pickerModalContent}>
        {nameField}

        {!isEdit && (
          <div className={styles.brandProducts}>
            <ProductPicker
              open={isOpen}
              selected={selected}
              onSelectedChange={setSelected}
              showSuggestions={false}
              withBrands={false}
            />
          </div>
        )}
      </ModalContent>

      <ModalFooter>
        <Button template="transparent" size="medium" onClick={onClose}>
          {t('rm:button.Cancel')}
        </Button>
        <Button
          template="primary"
          size="medium"
          disabled={!isEdit && !selectedRows.length}
          loading={isPending}
          onClick={handleSubmit}
        >
          {submitLabel}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
